/**
 * InvoiceCard Component - T097, T151
 *
 * Card display for invoice list items
 * Mobile-optimized with touch-friendly interactions
 */
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { GlassPanel } from '../ui/GlassPanel';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import type { Invoice } from '../../hooks/useInvoices';
import { formatCurrency, formatDate } from '@armard/shared';

interface InvoiceCardProps {
  invoice: Invoice;
  /** Compact mode for list views */
  compact?: boolean;
  /** Whether this card is selected */
  selected?: boolean;
  /** Toggle selection callback */
  onToggleSelect?: (id: string) => void;
}

export function InvoiceCard({ invoice, compact = false, selected, onToggleSelect }: InvoiceCardProps) {
  const isOverdue = invoice.status === 'sent' && new Date(invoice.dueDate) < new Date();
  const displayStatus = isOverdue ? 'overdue' : invoice.status;
  const hasSelection = onToggleSelect !== undefined;

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleSelect?.(invoice.id);
  };

  if (compact) {
    // Compact mobile-optimized card
    return (
      <Link to="/invoices/$id" params={{ id: invoice.id }}>
        <motion.div
          whileTap={{ scale: 0.98 }}
          className="block"
        >
          <GlassPanel
            hover
            className={`transition-all duration-200 active:bg-white/5 ${selected ? 'ring-1 ring-white/20 bg-white/5' : ''}`}
          >
            <div className="flex items-center justify-between gap-3">
              {hasSelection && (
                <input
                  type="checkbox"
                  checked={selected ?? false}
                  onClick={handleCheckboxClick}
                  onChange={() => {}}
                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-white accent-white/80 cursor-pointer shrink-0"
                />
              )}
              {/* Left: Invoice info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white truncate">{invoice.invoiceNumber}</span>
                  <InvoiceStatusBadge status={displayStatus} size="sm" />
                </div>
                <p className="text-sm text-white/50 truncate mt-0.5">
                  {invoice.customer?.companyName || 'Kunde'}
                </p>
              </div>

              {/* Right: Amount and date */}
              <div className="text-right shrink-0">
                <p className="font-semibold text-white">
                  {formatCurrency(parseFloat(invoice.grossTotal))}
                </p>
                <p className={`text-xs ${isOverdue ? 'text-red-400' : 'text-white/40'}`}>
                  {formatDate(invoice.dueDate)}
                </p>
              </div>

              {/* Chevron indicator */}
              <svg className="w-4 h-4 text-white/30 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </GlassPanel>
        </motion.div>
      </Link>
    );
  }

  // Full card
  return (
    <Link to="/invoices/$id" params={{ id: invoice.id }}>
      <motion.div
        whileTap={{ scale: 0.98 }}
        className="block h-full"
      >
        <GlassPanel
          hover
          className={`h-full transition-all duration-200 hover:border-white/20 active:bg-white/5 ${
            selected ? 'ring-1 ring-white/20 bg-white/5' : ''
          }`}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="min-w-0 flex-1 flex items-start gap-2">
              {hasSelection && (
                <input
                  type="checkbox"
                  checked={selected ?? false}
                  onClick={handleCheckboxClick}
                  onChange={() => {}}
                  className="w-4 h-4 mt-1 rounded border-white/20 bg-white/5 text-white accent-white/80 cursor-pointer shrink-0"
                />
              )}
              <div className="min-w-0">
                <h3 className="font-medium text-white truncate">{invoice.invoiceNumber}</h3>
                <p className="text-sm text-white/50 truncate">{invoice.customer?.companyName || 'Kunde'}</p>
              </div>
            </div>
            <InvoiceStatusBadge status={displayStatus} />
          </div>

          {/* Dates - stack on mobile, grid on larger screens */}
          <div className="flex flex-col sm:grid sm:grid-cols-2 gap-1 sm:gap-2 mb-3 text-sm">
            <div className="flex justify-between sm:block">
              <span className="text-white/40">Datum:</span>
              <span className="text-white/70 sm:ml-2">{formatDate(invoice.invoiceDate)}</span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-white/40">Fällig:</span>
              <span className={`sm:ml-2 ${isOverdue ? 'text-red-400' : 'text-white/70'}`}>
                {formatDate(invoice.dueDate)}
              </span>
            </div>
          </div>

          {/* Amount */}
          <div className="flex items-end justify-between pt-3 border-t border-white/5">
            <div className="text-sm text-white/40">
              {invoice.items.length} Position{invoice.items.length !== 1 ? 'en' : ''}
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold text-white">
                {formatCurrency(parseFloat(invoice.grossTotal))}
              </div>
              <div className="text-xs text-white/40">
                inkl. {parseFloat(invoice.vatRate)}% MwSt.
              </div>
            </div>
          </div>

          {/* Customer reference if present */}
          {invoice.customerReference && (
            <div className="mt-2 pt-2 border-t border-white/5">
              <span className="text-xs text-white/40 truncate block">Ref: {invoice.customerReference}</span>
            </div>
          )}
        </GlassPanel>
      </motion.div>
    </Link>
  );
}
